import { faCheckCircle, faDotCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Box, Skeleton } from '@mui/material';
import { Card } from '../Card';
import { CTA, CheckListItemCta } from './CheckListItemCta';
import { TMap } from '../../helpers/types';
import { DropDown, DropDownProps } from '../DropDown';
import { CSSProperties } from 'react';
import { colors } from '../colors';

export type StepState =
  | 'ready'
  | 'partially-ready'
  | 'not-ready'
  | 'not-available'
  | 'loading'
  | 'error';

export const stepStateColorsMap: TMap<StepState, string> = {
  ready: '#00CB20',
  'partially-ready': '#6367EF',
  'not-available': '#DEE2EB',
  'not-ready': '#B2B5BF',
  loading: '#fafafa',
  error: '#B2B5BF',
};

export type CheckListItemProps<T = number> = {
  step: StepState;
  component?: CheckListComponentProps<T>;
  title: string;
  error?: string;
  CTA?: CTA;
};

type CheckListComponentProps<T> =
  | { type: 'dropdown'; value: DropDownProps<T> }
  | {
      type: 'image';
      value: { src: string; style?: CSSProperties };
    }
  | { type: 'desc'; value: { caption: string } };

const CheckListComponent = <T,>(p: CheckListComponentProps<T>) => {
  switch (p.type) {
    case 'dropdown':
      return <DropDown {...p.value} />;
    case 'image':
      return <img {...p.value} />;
    case 'desc':
      return (
        <Box sx={{ textTransform: 'uppercase', color: '#999' }}>
          {p.value.caption}
        </Box>
      );
  }
};
export const CheckListItem = (p: CheckListItemProps) => (
  <Card
    variant="shadow"
    className="flex justify-between items-center gap-2 h-14"
    style={
      p.step === 'error' ? { border: `1px solid ${colors.danger}` } : undefined
    }
  >
    {p.step === 'loading' ? (
      <>
        <Skeleton
          height={35}
          sx={{ backgroundColor: stepStateColorsMap[p.step], width: '100%' }}
        />
      </>
    ) : (
      <>
        <Box className="flex flex-row gap-3" sx={{ alignItems: 'center' }}>
          <FontAwesomeIcon
            size="lg"
            icon={p.step === 'partially-ready' ? faDotCircle : faCheckCircle}
            color={stepStateColorsMap[p.step]}
          />
          <div className="font-semibold flex gap-1 items-center">
            <span className={p.step === 'ready' ? 'line-through' : undefined}>
              {p.title}
            </span>
            {p.component && <CheckListComponent {...p.component} />}
          </div>
        </Box>

        {p.CTA && (
          <CheckListItemCta
            {...p.CTA}
            disabled={p.step === 'not-available'}
            error={p.step === 'error'}
          />
        )}
      </>
    )}
  </Card>
);
